import React from 'react'
import { Link } from 'react-router-dom'

import { usePackagingEntitlement } from '../hooks/usePackagingEntitlement'
import { localDate, utcCalendarDate } from '../utils/date'

/**
 * Says up front that this tenant's plan cannot package, and what to do about it.
 *
 * Every modal that starts a packaging job shows this. Without it the refusal
 * only arrives as a 403 after the form has been filled in, which reads as a
 * failure rather than as a plan that does not include the feature.
 *
 * Renders nothing when packaging is available, so it is safe to place
 * unconditionally.
 */
/**
 * Whether an API error is one of the entitlement refusals from
 * services/entitlement.py (trial caps, free plan, suspended/cancelled/lapsed
 * subscription). The refusal arrives as a plain 403 detail string, so it is
 * recognised by wording: every refusal there names the subscription, the
 * trial, or the plan.
 */
export const isEntitlementRefusal = (message?: string | null): boolean =>
  /subscri|trial|plan/i.test(message ?? '')

/**
 * The way out of an entitlement refusal, styled for the red error banner the
 * refusal is shown in. Rendered next to the refusal by every modal that
 * starts a packaging job.
 */
export const EntitlementRefusalAction: React.FC = () => (
  <Link
    to="/settings/subscription"
    className="inline-block mt-2 px-3 py-1.5 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-500"
  >
    View plans
  </Link>
)

const SHORT_DATE: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' }

// The free plan's quota resets at 00:00 UTC on the first of the month, a calendar boundary
// shown as that date in every zone; the trial's resets with the trial itself, an instant.
const formatResetDate = (iso: string, plan: string | null): string =>
  plan === 'trial' ? localDate(iso, SHORT_DATE) : utcCalendarDate(iso, SHORT_DATE)

const PackagingEntitlementNotice: React.FC = () => {
  const { canPackage, reason, budget, plan } = usePackagingEntitlement()

  // A budgeted plan is told how much of the quota is left before it submits,
  // so a refusal at the cap is never a surprise.
  if (canPackage && budget) {
    return (
      <p role="status" className="mb-4 text-xs text-theme-text-secondary">
        {budget.apps_used} of {budget.apps_limit} custom apps and {budget.jobs_used} of{' '}
        {budget.jobs_limit} packaging jobs used
        {budget.resets_at ? ` — resets ${formatResetDate(budget.resets_at, plan)}` : ''}.
      </p>
    )
  }
  if (canPackage) return null

  return (
    <div
      role="status"
      className="mb-4 px-4 py-3 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20"
    >
      <p className="text-sm text-amber-800 dark:text-amber-300">
        {reason || 'Packaging requires a paid subscription.'}
      </p>
      <Link
        to="/settings/subscription"
        className="inline-block mt-2 text-sm font-medium text-amber-900 dark:text-amber-200 underline"
      >
        View plans
      </Link>
    </div>
  )
}

export default PackagingEntitlementNotice
